import React from 'react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { TimeEntryStatus } from '../../../core/models/TimeEntry';
import { format } from 'date-fns';
import { Clock, XCircle, DollarSign } from 'lucide-react';

interface TimeEntryCardProps {
  entry: TimeEntry;
  /** Display name or email for entry.userId (resolved by parent). */
  assigneeLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onReject?: () => void;
  showActions?: boolean;
}

export const TimeEntryCard: React.FC<TimeEntryCardProps> = ({
  entry,
  assigneeLabel,
  onEdit,
  onDelete,
  onReject,
  showActions = true,
}) => {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const getStatusColor = (status: TimeEntryStatus) => {
    switch (status) {
      case TimeEntryStatus.REJECTED:
        return 'error';
      case TimeEntryStatus.BILLED:
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: TimeEntryStatus) => {
    switch (status) {
      case TimeEntryStatus.REJECTED:
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant={getStatusColor(entry.status)}>
              <span className="flex items-center gap-1">
                {getStatusIcon(entry.status)}
                {entry.status}
              </span>
            </Badge>
            {entry.billable && (
              <Badge variant="default">
                <DollarSign className="h-3 w-3 mr-1" />
                Billable
              </Badge>
            )}
          </div>
          <p className="font-medium text-lg">{entry.description}</p>
          <div className="text-sm text-gray-600 mt-2 space-y-1">
            <p>
              <span className="font-medium">Duration:</span> {formatDuration(entry.duration)}
            </p>
            <p>
              <span className="font-medium">Time:</span>{' '}
              {format(new Date(entry.startTime), 'PPp')} -{' '}
              {format(new Date(entry.endTime), 'p')}
            </p>
            {(assigneeLabel || entry.userId) && (
              <p>
                <span className="font-medium">Attributed to:</span>{' '}
                {assigneeLabel || entry.userId}
              </p>
            )}
            {(entry.taskTitle || entry.taskId) && (
              <p>
                <span className="font-medium">Task:</span>{' '}
                {entry.taskTitle || entry.taskId}
              </p>
            )}
          </div>
        </div>
      </div>

      {showActions && (
        <div className="flex items-center gap-2 pt-3 border-t">
          {entry.status === TimeEntryStatus.PENDING && (
            onReject && (
              <Button variant="outline" size="sm" onClick={onReject}>
                Reject
              </Button>
            )
          )}
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
